import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight, Heart, Check } from 'lucide-react';
import { toast } from 'sonner';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setIsSubscribed(true);
    toast.success('Successfully subscribed! 🎉', {
      description: 'You\'ll receive the best deals in your inbox.'
    });
    setEmail('');
    setTimeout(() => setIsSubscribed(false), 3000);
  };
  
  return (
    <footer className="bg-slate-900 text-white">
      {/* CTA Section */}
      <div className="bg-gradient-to-r from-[#345a83] to-[#345a83]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
                Never Miss a Deal
              </h3>
              <p className="text-[#c5d5e8]">Get the best coupons delivered to your inbox</p>
            </div>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row items-center gap-3">
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="px-5 py-3.5 pr-12 bg-white/20 backdrop-blur-sm rounded-full text-white placeholder-white/70 w-64 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
                  data-testid="subscribe-email-input"
                />
                <Mail className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
              </div>
              <button 
                type="submit"
                disabled={isSubscribed}
                className={`px-6 py-3.5 rounded-full font-bold text-sm transition-all btn-press flex items-center gap-2 ${
                  isSubscribed 
                    ? 'bg-green-400 text-green-900' 
                    : 'bg-lime-400 text-slate-900 hover:bg-lime-300'
                }`}
                data-testid="subscribe-btn"
              >
                {isSubscribed ? (
                  <>
                    <Check size={16} />
                    Subscribed!
                  </>
                ) : (
                  <>
                    Subscribe
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img src="/logo.png" alt="Vuori Coupons Club" className="w-10 h-10" />
              <div className="flex flex-col leading-tight">
                <span className="font-heading font-bold text-lg">Vuori</span>
                <span className="font-heading font-semibold text-xs text-slate-400">Coupons Club</span>
              </div>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed">
              Your one-stop destination for the best deals, coupons, and cashback offers from top retailers.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Explore</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-slate-400 text-sm hover:text-[#4a7ab3] transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/stores" className="text-slate-400 text-sm hover:text-[#4a7ab3] transition-colors">
                  All Stores
                </Link>
              </li>
              <li>
                <Link to="/categories" className="text-slate-400 text-sm hover:text-[#4a7ab3] transition-colors">
                  Categories
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-slate-400 text-sm hover:text-[#4a7ab3] transition-colors">
                  Blog
                </Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Support</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/contact" className="text-slate-400 text-sm hover:text-[#4a7ab3] transition-colors">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/privacy-policy" className="text-slate-400 text-sm hover:text-[#4a7ab3] transition-colors">
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Popular Categories */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Popular</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/category/fashion" className="text-slate-400 text-sm hover:text-[#4a7ab3] transition-colors">
                  Fashion Deals
                </Link>
              </li>
              <li>
                <Link to="/category/tech" className="text-slate-400 text-sm hover:text-[#4a7ab3] transition-colors">
                  Tech Deals
                </Link>
              </li>
              <li>
                <Link to="/category/cosmetics" className="text-slate-400 text-sm hover:text-[#4a7ab3] transition-colors">
                  Beauty Deals
                </Link>
              </li>
              <li>
                <Link to="/category/travel" className="text-slate-400 text-sm hover:text-[#4a7ab3] transition-colors">
                  Travel Deals
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm text-center md:text-left">
              © {currentYear} Vuori Coupons Club. All rights reserved.
            </p>
            <p className="text-slate-500 text-sm flex items-center gap-1">
              Made with <Heart size={14} className="text-red-500 fill-red-500" /> for smart shoppers
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
